import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

import { Answer } from '@/components/common/Answer';
import { ExportButton } from '@/components/common/ExportButton';
import { FieldGuide } from '@/components/common/FieldGuide';
import { SkeletonLoader } from '@/components/common/StateViews';
import { Stat } from '@/pages/cade/Stat';
import { fetchCadeRegister } from '@/services/cade';
import type {
  CadeAmountLadder,
  CadeAppealCounts,
  CadeDatedDurations,
  CadeDecisionFlow,
  CadeSectorCount,
} from '@/services/types';

/** The register's own marker for "no procedure recorded". */
const UNRECORDED = 'UNRECORDED';

/**
 * The published register read at protocol grain: durations to the day, what the
 * Tribunal later did with the protocols the SG decided, appeals, TCC
 * contributions and sectors.
 *
 * Every panel is COUNTS or a quantile ladder beside its N, in the register's own
 * words. Nothing here is a rate that could be read as "the chance the Tribunal
 * overturns the SG" or "the chance of an appeal": the register does not say which
 * path a decision took, and the panels say that rather than leave it to inference.
 * It is the same population as the rest of this page (CADE em Numeros), never the
 * Tribunal session archive.
 */
export function RegisterPanels() {
  const { t } = useTranslation(['cade']);
  const { data, isPending, error } = useQuery({
    queryKey: ['cade', 'register'],
    queryFn: fetchCadeRegister,
  });

  if (isPending) return <SkeletonLoader rows={4} />;
  if (error) {
    return (
      <p role="alert" className="rounded-md border p-3 text-xs text-muted-foreground">
        {error.message}
      </p>
    );
  }

  return (
    <>
      <section className="rounded-lg border bg-card p-4" data-register-durations>
        <h2 className="mb-1 text-sm font-semibold text-foreground">{t('cade:register.durations.title')}</h2>
        <p className="mb-3 text-xs text-muted-foreground">{t('cade:register.durations.intro')}</p>
        <Answer answer={data.durations} help={t('cade:register.durations.help')}>
          {(durations: CadeDatedDurations) => <DaysTable durations={durations} />}
        </Answer>
        <div className="mt-3 flex justify-end">
          <ExportButton path="/api/v1/cade/export/register/durations" />
        </div>
      </section>

      <section className="rounded-lg border bg-card p-4" data-register-flow>
        <h2 className="mb-1 text-sm font-semibold text-foreground">{t('cade:register.flow.title')}</h2>
        <p className="mb-3 text-xs text-muted-foreground">{t('cade:register.flow.intro')}</p>
        <Answer answer={data.flow} help={t('cade:register.flow.help')}>
          {(flow: CadeDecisionFlow) => <FlowTable flow={flow} />}
        </Answer>
        <div className="mt-3 flex justify-end">
          <ExportButton path="/api/v1/cade/export/register/flow" />
        </div>
        <div className="mt-3">
          <FieldGuide i18nKey="cade:register.flow.guide" />
        </div>
      </section>

      <section className="rounded-lg border bg-card p-4" data-register-appeals>
        <h2 className="mb-1 text-sm font-semibold text-foreground">{t('cade:register.appeals.title')}</h2>
        <p className="mb-3 text-xs text-muted-foreground">{t('cade:register.appeals.intro')}</p>
        <Answer answer={data.appeals} help={t('cade:register.appeals.help')}>
          {(appeals: CadeAppealCounts) => <AppealPanel appeals={appeals} />}
        </Answer>
      </section>

      <section className="rounded-lg border bg-card p-4" data-register-tcc>
        <h2 className="mb-1 text-sm font-semibold text-foreground">{t('cade:register.tcc.title')}</h2>
        <p className="mb-3 text-xs text-muted-foreground">{t('cade:register.tcc.intro')}</p>
        <Answer answer={data.tcc_contributions} help={t('cade:register.tcc.help')}>
          {(ladder: CadeAmountLadder) => <TccPanel ladder={ladder} />}
        </Answer>
      </section>

      <section className="rounded-lg border bg-card p-4" data-register-sectors>
        <h2 className="mb-1 text-sm font-semibold text-foreground">{t('cade:register.sectors.title')}</h2>
        <p className="mb-3 text-xs text-muted-foreground">{t('cade:register.sectors.intro')}</p>
        <Answer answer={data.sectors} help={t('cade:register.sectors.help')}>
          {(sectors: CadeSectorCount[]) => <SectorList sectors={sectors} />}
        </Answer>
      </section>
    </>
  );
}

function DaysTable({ durations }: { durations: CadeDatedDurations }) {
  const { t, i18n } = useTranslation(['cade']);
  const number = new Intl.NumberFormat(i18n.language);
  const years = new Intl.NumberFormat(i18n.language, { maximumFractionDigits: 1 });
  const days = (value: number | null) => (value === null ? '' : number.format(Math.round(value)));

  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead className="bg-muted/50 text-left text-xs text-muted-foreground">
          <tr>
            <th className="px-3 py-2 font-medium">{t('cade:register.durations.columns.procedure')}</th>
            <th className="px-3 py-2 text-right font-medium">{t('cade:register.durations.columns.protocols')}</th>
            <th className="px-3 py-2 text-right font-medium">{t('cade:register.durations.columns.dated')}</th>
            <th className="px-3 py-2 text-right font-medium">p25</th>
            <th className="px-3 py-2 text-right font-medium">{t('cade:register.durations.columns.median')}</th>
            <th className="px-3 py-2 text-right font-medium">p75</th>
            <th className="px-3 py-2 text-right font-medium">p90</th>
          </tr>
        </thead>
        <tbody>
          {durations.ladders.map((row) => (
            <tr key={row.procedure} className="border-t hover:bg-muted/30">
              <td className="px-3 py-2 text-foreground">
                {row.procedure === UNRECORDED ? t('cade:register.unrecorded') : row.procedure}
              </td>
              <td className="px-3 py-2 text-right tabular-nums text-muted-foreground">
                {number.format(row.protocols)}
              </td>
              <td className="px-3 py-2 text-right tabular-nums text-muted-foreground">
                {number.format(row.dated)}
              </td>
              {row.below_floor ? (
                <td colSpan={4} className="px-3 py-2 text-xs italic text-muted-foreground">
                  {t('cade:register.durations.belowFloor')}
                </td>
              ) : (
                <>
                  <td className="px-3 py-2 text-right tabular-nums text-muted-foreground">{days(row.p25)}</td>
                  <td className="px-3 py-2 text-right tabular-nums font-semibold text-foreground">
                    {days(row.median)}
                    <span className="ml-1 text-xs font-normal text-muted-foreground">
                      ({years.format((row.median ?? 0) / 365.25)} {t('cade:register.durations.yearsAbbr')})
                    </span>
                  </td>
                  <td className="px-3 py-2 text-right tabular-nums text-muted-foreground">{days(row.p75)}</td>
                  <td className="px-3 py-2 text-right tabular-nums text-muted-foreground">{days(row.p90)}</td>
                </>
              )}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function FlowTable({ flow }: { flow: CadeDecisionFlow }) {
  const { t, i18n } = useTranslation(['cade']);
  const number = new Intl.NumberFormat(i18n.language);
  // One row per SG outcome, in the order the repository returned them (by name,
  // then count): never re-sorted by how often an outcome reached the Tribunal.
  const bySg = new Map<string, { none: number; later: { outcome: string; protocols: number }[] }>();
  for (const cell of flow.cells) {
    const entry = bySg.get(cell.sg_outcome) ?? { none: 0, later: [] };
    if (cell.tribunal_outcome === null) entry.none += cell.protocols;
    else entry.later.push({ outcome: cell.tribunal_outcome, protocols: cell.protocols });
    bySg.set(cell.sg_outcome, entry);
  }

  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead className="bg-muted/50 text-left text-xs text-muted-foreground">
          <tr>
            <th className="px-3 py-2 font-medium">{t('cade:register.flow.columns.sg')}</th>
            <th className="px-3 py-2 text-right font-medium">{t('cade:register.flow.columns.protocols')}</th>
            <th className="px-3 py-2 text-right font-medium">{t('cade:register.flow.columns.none')}</th>
            <th className="px-3 py-2 font-medium">{t('cade:register.flow.columns.later')}</th>
          </tr>
        </thead>
        <tbody>
          {[...bySg.entries()].map(([sg, entry]) => (
            <tr key={sg} className="border-t align-top hover:bg-muted/30">
              <td className="px-3 py-2 text-foreground">{sg}</td>
              <td className="px-3 py-2 text-right tabular-nums text-foreground">
                {number.format(entry.none + entry.later.reduce((sum, l) => sum + l.protocols, 0))}
              </td>
              <td className="px-3 py-2 text-right tabular-nums text-muted-foreground">
                {number.format(entry.none)}
              </td>
              <td className="px-3 py-2 text-xs text-muted-foreground">
                {entry.later.length === 0
                  ? '—'
                  : entry.later.map((l) => `${l.outcome}: ${number.format(l.protocols)}`).join(' · ')}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function AppealPanel({ appeals }: { appeals: CadeAppealCounts }) {
  const { t, i18n } = useTranslation(['cade']);
  const number = new Intl.NumberFormat(i18n.language);
  return (
    <dl className="grid grid-cols-2 gap-3 text-xs sm:grid-cols-3">
      <Stat label={t('cade:register.appeals.decided')} value={number.format(appeals.tribunal_decided)} />
      <Stat label={t('cade:register.appeals.with')} value={number.format(appeals.with_appeal)} />
      <Stat label={t('cade:register.appeals.without')} value={number.format(appeals.without_appeal)} />
      <Stat label={t('cade:register.appeals.unknown')} value={number.format(appeals.appeal_unknown)} />
      <Stat label={t('cade:register.appeals.embargos')} value={number.format(appeals.embargos)} />
      <Stat label={t('cade:register.appeals.reconsiderations')} value={number.format(appeals.reconsiderations)} />
    </dl>
  );
}

function TccPanel({ ladder }: { ladder: CadeAmountLadder }) {
  const { t, i18n } = useTranslation(['cade']);
  const number = new Intl.NumberFormat(i18n.language);
  const money = new Intl.NumberFormat(i18n.language, {
    style: 'currency',
    currency: 'BRL',
    maximumFractionDigits: 0,
  });
  return (
    <dl className="grid grid-cols-2 gap-3 text-xs sm:grid-cols-4">
      <Stat
        label={t('cade:register.tcc.n')}
        value={`${number.format(ladder.n)} / ${number.format(ladder.protocols)}`}
      />
      <Stat label="p25" value={money.format(ladder.p25)} />
      <Stat label={t('cade:register.tcc.median')} value={money.format(ladder.median)} />
      <Stat label="p75" value={money.format(ladder.p75)} />
      <Stat label="p90" value={money.format(ladder.p90)} />
      <Stat label={t('cade:register.tcc.max')} value={money.format(ladder.maximum)} />
    </dl>
  );
}

function SectorList({ sectors }: { sectors: CadeSectorCount[] }) {
  const { i18n } = useTranslation(['cade']);
  const number = new Intl.NumberFormat(i18n.language);
  return (
    <ul className="space-y-1 text-sm">
      {sectors.map((s) => (
        <li key={s.section} className="flex items-baseline justify-between gap-3">
          <span className="text-foreground">{s.section}</span>
          <span className="tabular-nums text-muted-foreground">{number.format(s.protocols)}</span>
        </li>
      ))}
    </ul>
  );
}
